import { css } from "@emotion/react";
import { Link } from "gatsby";
import React from "react";
import { mediaQueries, pxToRem, weights, transition } from "../utils/css-utils";
import { focusStyles } from "../utils/global-styles";
import { colors } from "../utils/colors";
import GridContainer from "./GridContainer";
import GridCol from "./GridCol";

type FooterProps = {};

const Footer: React.FC<FooterProps> = () => {
  const footerWrapperStyles = css`
    /* margin-top: ${pxToRem(72)}; */
    margin-bottom: ${pxToRem(32)};
    font-family: "Space Grotesk";

    span,
    a {
      color: ${colors.black200};
    }
  `;

  const logoStyles = css`
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: ${pxToRem(8)};
    margin-bottom: 48px;

    a {
      position: relative;
      height: ${pxToRem(32)};
      
      &:focus-visible {
        ${focusStyles}
      }
    }

    img {
      object-fit: scale-down;
    }

    ${mediaQueries.md} {
      a {
        height: 52px;
      }
    }

    ${mediaQueries.lg} {
      margin-bottom: ${pxToRem(48)};
    }
  `;

  const rule = css`
    height: ${pxToRem(1)};
    background-color: ${colors.black600};
    margin-top: ${pxToRem(24)};
    margin-bottom: ${pxToRem(24)};
  `;

  const linkStyles = css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: ${pxToRem(24)};
    margin-bottom: ${pxToRem(32)};
    font-size: 13px;
    color: #8b9397;
    text-align: center;

    a {
      text-decoration: none;
      transition: ${transition()};

      &:hover {
        color: ${colors.violet300};
      }
      
      &:active {
        color: ${colors.violet400};
      }
    }

    span {
      color: ${colors.black400};
    }

    div {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
      gap: ${pxToRem(24)};
    }

    ${mediaQueries.md} {
      justify-content: flex-start;

      div {
        justify-content: flex-start;
      }
    }

    ${mediaQueries.lg} {
      div {
        margin-left: auto;
      }
    }
  `;

  const firstRowStyles = css`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;

    //Logo full width
    > div:first-of-type {
      flex: 0 0 100%;
    }

    //Rows wrap, each 50%
    > div:nth-of-type(2), div:nth-of-type(3), div:nth-of-type(4) {
      flex: 0 0 50%;
    }

    //Discord button at bottom
    > div:nth-of-type(5) {
      flex: 0 0 100%;
    }

    //Social
    > div:last-of-type {
      flex: 0 0 100%;
    }

    ${mediaQueries.md} {
      margin-bottom: ${pxToRem(24)};
      justify-content: space-between;
      
      //Logo
      > div:first-of-type {
        flex: 0 0 50%;
        order: 1;
      }

      //Rows
      > div:nth-of-type(2), div:nth-of-type(3), div:nth-of-type(4) {
        flex: 0 0 33%;
        order: 3;
      }

      //Discord button
      > div:nth-of-type(5) {
        order: 2;
        flex: 0 0 30%;
      }

      //Social
      > div:last-of-type {
        order: 3;
        flex: 0 0 100%;
      }
    }

    ${mediaQueries.lg} {
      justify-content: space-between;
      > div:first-of-type, > div:nth-of-type(2), > div:nth-of-type(3), > div:nth-of-type(4), > div:last-of-type {
        flex: 0 0 auto;
        order: unset;
      }

      //Discord button
      > div:nth-of-type(5) {
        order: 2;
      }

      //Social
      > div:last-of-type {
        order: 3;
        flex: 0 0 100%;
      }
    }
  `;

  const communityBtn = css`
    display: inline-flex;
    text-decoration: none;
    align-items: center;
    border: ${pxToRem(2)} solid ${colors.borderDefault};
    border-radius: ${pxToRem(40)};
    white-space: nowrap;
    svg {
      margin-right: ${pxToRem(8)};
    }
    padding: ${pxToRem(4)} ${pxToRem(16)};
    font-size: 16px;
    font-weight: ${weights.bold};
    color: #fff;

    ${mediaQueries.md} {
      padding: ${pxToRem(8)} ${pxToRem(24)};
    }
  `;

  const socialIconsContainer = css`
    display: flex;
    justify-content: center;
    gap: 36px;
    margin-top: ${pxToRem(40)};
    ${mediaQueries.md} {
      justify-content: flex-start;
    }

    ${mediaQueries.lg} {
      justify-content: flex-end;
    }
  `;

  const linksRow = css`
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
    font-size: 16px;
    strong {
      color: #fff;
    }

    a {
      text-decoration: none;
      color: #d8dbdc;
      
      transition: ${transition()};

      &:hover {
        color: ${colors.violet300};
      }
      
      &:active {
        color: ${colors.violet400};
      }
    }

    ${mediaQueries.md} {
      margin-bottom: 0;
    }
  `;

  const communityBtnContainer = css`
    display: flex;
    justify-content: center;

    ${mediaQueries.md} {
      margin: 0;
      justify-content: flex-end;
    }
  `;

  const cookieSettings = css`
    cursor: pointer;
  `;
  
  const currentYear = new Date().getFullYear();

  return (
    <GridContainer>
      <GridCol colStart={1} colEnd={13}>
        <div css={footerWrapperStyles}>
          <div css={firstRowStyles}>
            <div css={logoStyles}>
              <Link to="/">
                <img src="/logos/logo_horizontal.svg" alt="Upsun Logo" />
              </Link>
            </div>
            <div css={linksRow}>
              <strong>Company</strong>
              <Link to="/about-us/">About us</Link>
              <Link to="/faq/">FAQ</Link>
            </div>
            <div css={linksRow}>
              <strong>Product</strong>
              <Link to="/features/">Features</Link>
              <Link to="/pricing/">Pricing</Link>
            </div>
            <div css={linksRow}>
              <strong>Resources</strong>
              <Link to="/blog/">Blog</Link>
              <a href="https://docs.upsun.com/">Documentation</a>
              <a href="https://console.upsun.com/-/users/:user/tickets/open">Support</a>
            </div>
            <div css={communityBtnContainer}>
              <a href="https://discord.gg/PkMc2pVCDV" css={communityBtn}>
                <svg
                  width="16"
                  height="14"
                  viewBox="0 0 16 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    id="Vector"
                    d="M13.5535 1.95307C12.5027 1.47234 11.3928 1.13004 10.2526 0.9375C10.0966 1.21688 9.95561 1.5038 9.82976 1.79702C8.61535 1.61455 7.38081 1.61455 6.16641 1.79702C6.04056 1.5038 5.89961 1.21688 5.74357 0.9375C4.60341 1.13256 3.49219 1.47486 2.44013 1.95559C0.351096 5.04635 -0.215207 8.06034 0.0679444 11.0315C1.29116 11.9351 2.66036 12.6235 4.11639 13.064C4.44358 12.6235 4.73429 12.1553 4.98346 11.6658C4.51028 11.4896 4.05346 11.2707 3.61804 11.0139C3.73256 10.9309 3.84456 10.8453 3.95279 10.7622C6.51626 11.9678 9.48369 11.9678 12.0472 10.7622C12.1566 10.8516 12.2686 10.9372 12.3819 11.0139C11.9465 11.2719 11.4884 11.4896 11.014 11.6671C11.2631 12.1566 11.5526 12.6235 11.881 13.064C13.3383 12.6248 14.7088 11.9376 15.932 11.0328C16.2642 7.58716 15.3644 4.60086 13.5535 1.95307ZM5.34212 9.20428C4.55307 9.20428 3.90119 8.48822 3.90119 7.6073C3.90119 6.72638 4.53042 6.00403 5.3396 6.00403C6.14879 6.00403 6.79563 6.72638 6.78179 7.6073C6.76795 8.48822 6.14627 9.20428 5.34212 9.20428ZM10.6578 9.20428C9.86752 9.20428 9.21815 8.48822 9.21815 7.6073C9.21815 6.72638 9.84738 6.00403 10.6578 6.00403C11.4683 6.00403 12.1101 6.72638 12.0962 7.6073C12.0824 8.48822 11.462 9.20428 10.6578 9.20428Z"
                    fill="white"
                  />
                </svg>
                Join the community
              </a>
            </div>
            <div css={socialIconsContainer}>
              <a href="https://twitter.com/upsuncom" title="Upsun's twitter account">
                <img src="/icons/x.svg" alt=""/>
              </a>
              <a href="https://www.linkedin.com/company/upsuncom/" title="Upsun's linkedin account">
                <img src="/icons/linkedin.svg" alt=""/>
              </a>
              <a href="https://github.com/upsun" title="Upsun's github account">
                <img src="/icons/github.svg" alt=""/>
              </a>
              <a href="https://www.youtube.com/@upsun_com" title="Upsun's youtube account">
                <img src="/icons/youtube.svg" alt=""/>
              </a>
              <a href="https://www.tiktok.com/@upsun.com" title="Upsun's tiktok account">
                <img src="/icons/tiktok.svg" alt=""/>
              </a>
            </div>
          </div>
          <div css={rule}></div>
          <div css={linkStyles}>
            <span>© {currentYear} <a href="https://platform.sh/">Platform.sh</a>. All rights reserved.</span>
            <div>
              <a href="https://platform.sh/trust-center/legal/tos/">
                Terms of Service
              </a>
              <a href="https://platform.sh/trust-center/privacy/privacy-policy/">
                Privacy
              </a>
              <a href="https://platform.sh/trust-center/legal/impressum/">
                Impressum
              </a>
              <a href="https://platform.sh/trust-center/legal/wcag/">
                WCAG Compliance
              </a>
              <a href="https://platform.sh/trust-center/security/responsible-disclosure/">
                Report a security issue
              </a>
              <a className="ot-sdk-show-settings" css={cookieSettings}>
                Manage your cookie preferences
              </a>
            </div>
          </div>
        </div>
      </GridCol>
    </GridContainer>
  );
};

export default Footer;
